/**
 * studio-ai client — CreativeStudio's AI calls.
 *
 * The AI runs in a Supabase Edge Function (supabase/functions/studio-ai). Everything is sent
 * as base64 and comes back as base64: the image is never uploaded to storage, never given a
 * public URL, and never persisted server-side. It leaves the device only for the duration of
 * the request the user asked for.
 *
 * Both env vars are optional. With them unset the studio runs fully offline and the AI panel
 * shows a short notice instead of failing on every click.
 */
// VITE_SUPABASE_URL is the platform convention (OneAdmin derives the Supabase project ref
// from it by regex, and its Stage-4 function deploy fails without it), so that is the variable
// to set. The functions endpoint is always <project-url>/functions/v1, so it is derived rather
// than configured twice — two variables that must agree is two chances to disagree.
// VITE_SUPABASE_FUNCTIONS_URL stays supported as an override for a self-hosted or proxied
// functions endpoint that isn't on the project domain.
import { isOnline } from './net';

// PCake: the studio's AI is CreativeStudio's own studio-ai function, in CreativeStudio's
// Supabase project (not OneAdmin's — studio-ai is not deployed there). That project is the
// default, so PCake needs no setting.
const ONEADMIN_PROJECT_URL = 'https://byjglozsepsdjjefbpgi.supabase.co';
// Fixed here on purpose: PCake's build carries OneAdmin's own VITE_SUPABASE_* values, and
// sending OneAdmin's key to this project is a 401. (No env variable, so OneAdmin asks for none.)
const PROJECT_URL = ONEADMIN_PROJECT_URL;
const FUNCTIONS_URL =
  (PROJECT_URL ? `${PROJECT_URL}/functions/v1` : '')
    .replace(/\/+$/, '');
// CreativeStudio's public anon key (it ships in CreativeStudio's own bundle). The env vars override it.
const ONEADMIN_PUBLISHABLE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ5amdsb3pzZXBzZGpqZWZicGdpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODg3MjA2MjAsImV4cCI6MjEwNDI5NjYyMH0.-GHDRjnGiPAwnhPISXxlLVJ8rhRpC5qrBayCNfCkv2w';
const ANON_KEY = ONEADMIN_PUBLISHABLE_KEY;

// Who is asking, for the daily allowance the server counts (see studio-ai → OneAdmin's
// app-meter). Signed in, that is the account's own token; signed out, a per-device id, so one
// browser cannot have a fresh allowance every time the page is reloaded. Neither is stored
// anywhere but here and the counter; the server never sees an address it keeps.
const DEVICE_KEY = 'pcake.studio.device';
const deviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() || `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch { return ''; }   // private mode: the address side of the count still applies
};

// The signed-in session's token, when there is one. Imported lazily so this module keeps
// working in a build with no auth configured at all.
let _sessionToken = () => '';
export const setAiSessionTokenSource = (fn) => { _sessionToken = typeof fn === 'function' ? fn : () => ''; };

export const getBackendUrl = () => FUNCTIONS_URL;

/** Is the AI configured for this build at all? Nothing to do with whether it is reachable. */
export const isAiEnabled = () => Boolean(FUNCTIONS_URL);

/**
 * Configured AND there is a connection to use it over.
 *
 * The two are worth keeping apart. "Not configured" is a build that never had AI and should not
 * advertise it; "configured but offline" is a button that belongs on screen, disabled, saying
 * it needs a connection — so that when the connection comes back the person knows what will
 * work again. Call sites that decide whether to SHOW something use isAiEnabled; call sites that
 * decide whether it can be pressed NOW use this.
 */
export const isAiReachable = () => Boolean(FUNCTIONS_URL) && isOnline();

/**
 * Call the studio-ai function.
 *
 * @param {'edit'|'remove-bg'|'classify'|'read-text'} action
 * @param {object} payload  action-specific fields (imageBase64, prompt, …)
 * @returns {Promise<object>} the function's JSON body on success
 * @throws {Error} with a message fit to show the user
 */
export async function callStudioAi(action, payload = {}) {
  if (!FUNCTIONS_URL) {
    throw new Error(
      'AI tools are not set up for this build. Add VITE_SUPABASE_URL and ' +
      'VITE_SUPABASE_ANON_KEY, and deploy the studio-ai function.',
    );
  }

  // ── Do not make someone wait to be told there is no connection ──────────────────────────
  // Offline, the two posts below each sit through the browser's own connect timeout before
  // rejecting. That is up to half a minute of "Detecting object…" on a phone with no signal,
  // ending in a message about reaching a service. Smart Crop in particular has a perfectly
  // good on-device method waiting behind this call, so the wait buys nothing at all. The
  // error is the same one the retry path raises, so every call site already handles it.
  if (!isOnline()) {
    throw new Error('Could not reach the AI service. Check your connection and try again.');
  }

  const body = JSON.stringify({ action, ...payload });
  const baseHeaders = {
    'Content-Type': 'application/json',
    ...(ANON_KEY ? { apikey: ANON_KEY } : {}),
    // The account's own token when signed in — that is what tells the server this caller
    // gets the larger allowance — and the anon key otherwise, which is all the gateway
    // needs to let the request through.
    Authorization: `Bearer ${_sessionToken() || ANON_KEY}`,
  };
  const post = (headers) => fetch(`${FUNCTIONS_URL}/studio-ai`, { method: 'POST', headers, body });

  let res;
  try {
    res = await post({ ...baseHeaders, ...(deviceId() ? { 'x-cs-device': deviceId() } : {}) });
  } catch {
    // ── One retry, without the device id ─────────────────────────────────────────────────
    // A browser may only send a header the server has said it will accept. x-cs-device was
    // added for metering without being added to the function's Access-Control-Allow-Headers,
    // so from that day every AI call from a browser was refused at the preflight — before the
    // request was even made. fetch rejects, which looks exactly like being offline, so the app
    // said "could not reach the AI service" about a service that was answering perfectly.
    //
    // The function's header list is fixed too, but that needs a deploy, and until it lands this
    // keeps the AI working: the device id is for counting usage, and losing the count is worth
    // rather less than losing background removal, AI edit, and the cut-out that Smart Crop
    // depends on to separate a face from a wall.
    try {
      res = await post(baseHeaders);
    } catch {
      // Deliberately NOT treated as proof that the connection is gone: a refused CORS preflight
      // rejects with exactly these words, and this app has shipped that bug before. See net.js.
      throw new Error('Could not reach the AI service. Check your connection and try again.');
    }
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.error || `AI request failed (${res.status}).`);
  }
  return data;
}

// ── Kling's second half ──────────────────────────────────────────────────────
// Kling is a submit-then-poll service that can take two minutes, and a Supabase function is
// cut off at 150 seconds, so the server cannot both start the job and wait for it: an edit
// asking for Kling answers with a task id. The two halves are rejoined HERE rather than at
// the call sites, so aiEditImage still resolves with a data URL and nothing upstream has to
// know that one of the editors works differently from the other two.
const TASK_POLL_MS = 3000;
// A wall-clock cap, not a tick count. Counting ticks looks like three minutes and is not: each
// tick is the wait PLUS a round trip the server gives up to thirty seconds, so sixty of them
// is most of an hour with the editor locked and a spinner going. What matters is how long the
// person has been waiting, so that is what is measured.
const TASK_DEADLINE_MS = 240_000;
// One bad tick is not a dead job. The task is already paid for and still running on Kling's
// side, so a single 502 from the gateway or one slow status call must not throw it away — but
// a service that has failed three times in a row is not coming back inside this edit.
const TASK_POLL_SLIPS = 3;

async function joinPendingTask(first, { onWait } = {}) {
  if (!first?.pending || !first?.taskId) return first;          // not a two-part answer
  const until = Date.now() + TASK_DEADLINE_MS;
  let slips = 0, lastSlip = null, ticks = 0;
  while (Date.now() < until) {
    await new Promise(r => setTimeout(r, TASK_POLL_MS));
    // Going offline mid-job is worth saying at once rather than after a run of failed ticks.
    if (!isOnline()) throw new Error('The connection dropped while the AI was working. Try again.');
    let d;
    try {
      d = await callStudioAi('kling-status', { taskId: first.taskId });
      slips = 0;
    } catch (e) {
      // The server's own words when it has a verdict; a transient failure otherwise. There is
      // no way to tell them apart from here, so the rule is how MANY in a row rather than
      // which: three consecutive and the message that came with the last one is the answer.
      lastSlip = e;
      if (++slips >= TASK_POLL_SLIPS) throw e;
      continue;
    }
    if (!d.pending) return d;
    try { onWait?.(++ticks); } catch { /* a progress callback must never fail the job */ }
  }
  throw lastSlip || new Error('The AI is taking longer than expected. Try again in a moment.');
}

/** Convenience wrappers, so call sites read as what they do. */
// sketchGuided: true for the canvas's "whole sheet" edit (a hand-drawn sketch flattened onto
// photo material) -- tells the backend to treat the sketch's own outline/pose as the target
// composition instead of preserving the input's framing, which is what it does by default.
// provider: normally left alone, so the server picks (AI_IMAGE_PROVIDER, default Gemini
// first). Set to 'kling' by the three-strikes rule in ImageStudio — the same instruction run
// a third time on the same layer — which is the one case the client knows about and the
// server cannot.
// imageWidth/imageHeight: the source's true pixel size, so Kling can be asked for the
// nearest aspect ratio it supports instead of guessing one.
// onWait: called on each poll tick while a two-part job is running, for the waiting message.
export const aiEditImage = (imageBase64, prompt, referenceImageBase64, opts = {}) =>
  callStudioAi('edit', {
    imageBase64, prompt,
    ...(referenceImageBase64 ? { referenceImageBase64 } : {}),
    ...(opts.sketchGuided ? { sketchGuided: true } : {}),
    ...(opts.provider ? { provider: opts.provider } : {}),
    ...(opts.imageWidth > 0 && opts.imageHeight > 0
      ? { imageWidth: opts.imageWidth, imageHeight: opts.imageHeight } : {}),
  }).then(d => joinPendingTask(d, opts)).then(d => d.imageBase64);

/** Make a picture from words alone — for an empty canvas, where there is nothing to edit. */
export const aiGenerateImage = (prompt, aspect, opts = {}) =>
  callStudioAi('generate', {
    prompt,
    ...(aspect ? { aspect } : {}),
    ...(opts.provider ? { provider: opts.provider } : {}),
  }).then(d => joinPendingTask(d, opts)).then(d => d.imageBase64);

// `prefer: 'backdrop'` — auto crop: skip the object-list route and ask for the backdrop first.
// `prefer: 'mask'` — one subject is wanted, so skip the object-list route. It exists for auto
// crop, which needs every object separately; for anything already pointed at it spends a call
// from a small daily allowance to answer a question nobody asked.
export const aiRemoveBackground = (imageBase64, { prefer } = {}) =>
  callStudioAi('remove-bg', { imageBase64, ...(prefer ? { prefer } : {}) }).then(d => d.imageBase64);

// The separate things a picture is made of, each with its own mask: [{ label, box_2d, mask }],
// box_2d as [y0, x0, y1, x1] normalised to 0-1000 of the picture sent, mask a PNG the size of
// that box. For a picture that is already a cut-out, where remove-bg can only answer "all of it".
export const aiFindObjects = (imageBase64) =>
  callStudioAi('objects', { imageBase64 }).then(d => (Array.isArray(d.objects) ? d.objects : []));

export const aiReadText = (imageBase64) =>
  callStudioAi('read-text', { imageBase64 }).then(d => String(d.text || ''));

export const aiDescribe = (imageBase64, question) =>
  callStudioAi('classify', { imageBase64, ...(question ? { question } : {}) }).then(d => String(d.text || ''));

/**
 * What the deployed function can see — never any part of a key, only whether one is there.
 *
 * A secret set in the dashboard but not picked up by the running function is invisible from
 * out here: the cut-out just falls through to the next route and reports THAT one's failure.
 * This says, in one call, which build is live and which services it has keys for.
 */
export const aiDiagnostics = () => callStudioAi('diag');

// Reachable from the browser console while developing: `await window.csAiDiag()`.
if (import.meta.env?.DEV && typeof window !== 'undefined') window.csAiDiag = aiDiagnostics;
